/* eslint-disable eqeqeq */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import async from 'async';
import _ from 'lodash';
import { connect } from 'react-redux';
import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  formatNumeral, handlePromise, getCountryName, formatDataPhoneToSend,
} from 'utils';
import RateService from 'api/Rate';
import CampaignService from 'api/Campaign';
import AccountService from 'api/Account';
import UserService from 'api/User';
import CrmIntegrationService from 'api/CrmIntegration';
import SettingsActions from 'store/reducers/Settings';
import AccountActions from 'store/reducers/Account';
import UserActions from 'store/reducers/User';
import NewCampaignView from 'views/NewCampaign2';
import PhoneWarning from 'views/NewCampaign2/PhoneWarning';
import { ConfirmationModal } from 'components';
import { useSubmit, useFetch } from 'hooks';


const NewCampaign = ( {
  history, locale, accountFirstPrice, updateAccountInfo, updateUser, toggleLoading, user, toggleErrorAlert,
} ) => {
  const [dataLoading, setDataLoading] = useState( false );
  const [tariffs, setTariffs] = useState( [] );
  const [crm, setCrm] = useState( null );
  const [draftCampaign, setDraftCampaign] = useState( {} );
  const [errorModalOpened, setErrorModalOpened] = useState( false );
  const [successModalOpened, setSuccessModalOpened] = useState( false );
  const [blockNavigationAlert, setBlockNavigationAlert] = useState( false );
  const errorTextRef = useRef( null );

  const [dataTeampleader, TeampleaderdataLoading] = useFetch({
    initialState: {},
    promise: () => CrmIntegrationService.checkIntegrationStatus({name:"teamleader"}),
    toggleErrorAlert,
    deps: []
  });

  const toggleErrorModal = useCallback( ( text ) => {
    errorTextRef.current = text;
    setErrorModalOpened( !errorModalOpened );
  }, [errorModalOpened] );

  const toggleSuccessModal = useCallback( () => {
    setSuccessModalOpened( !successModalOpened );
  }, [successModalOpened] );

  useEffect( () => {
    setBlockNavigationAlert( false );
  }, [] );

  useEffect( ( ) => {
    setDataLoading( true );
    async.parallel( {
      tariffs: async ( cb ) => {
        const [, response, responseData] = await handlePromise(
          RateService.getRates( { 'filters[active]': '1', limit: -1 } ),
        );
        if ( !response.ok ) return cb();
        let formattedData = responseData ? responseData.data : [];
        if ( formattedData ) {
          formattedData = _.map( formattedData, ( item ) => {
            const country = getCountryName( item.country, locale );
            const codePhone = item.codePhone ? `(+${item.codePhone})` : '';
            return {
              ...item,
              countryName: `${country || item.country || '-'} ${codePhone}`,
            };
          } );
        }
        return cb( null, formattedData );
      },
      draft: async ( cb ) => {
        const [, response, responseData] = await handlePromise(
          CampaignService.getWizardCampaign(),
        );
        if ( !response.ok ) return cb();
        return cb( null, _.get( responseData, 'data[0]', null ) );
      },
      crm: async ( cb ) => {
        const [, response, responseData] = await handlePromise(
          CrmIntegrationService.getIntegrations( { limit: -1, 'filters[active]': '1' } ),
        );
        if ( !response.ok ) return cb();
        if ( responseData && responseData.data && _.find( responseData.data, { name: 'teamleader' } ) ) {
          return cb( null, { teamleader: true } );
        }
        return cb();
      },
    }, ( error, results ) => {
      if ( results.tariffs ) setTariffs( results.tariffs );
      if ( results.draft ) setDraftCampaign( results.draft );
      if ( results.crm ) setCrm( results.crm );
      setDataLoading( false );
    } );
  }, [] ); // eslint-disable-line

  const submitCampaign = useCallback( async ( formData ) => {
    let date = `${moment( formData.startDate ).format( 'MM/DD/YYYY' )} ${moment( formData.startTime ).format( 'HH:mm' )}`;
    if ( moment.tz( date, 'MM/DD/YYYY HH:mm', true, formData.timeZone ).diff( moment.tz( formData.timeZone ), 'minutes' ) <= 0 ) {
      date = moment.tz( formData.timeZone );
    }
    const selectedContacts = _.filter( formData.contacts, { selected: true } );
    const mediaAudios = [];
    if ( formData.singleFile ) {
      mediaAudios.push( {
        audioType: 'message_audio',
        id: formData.singleFile.id,
      } );
    }
    if ( formData.introductionFile ) {
      mediaAudios.push( {
        audioType: 'intro_audio',
        id: formData.introductionFile.id,
      } );
    }
    if ( formData.optionsFile ) {
      mediaAudios.push( {
        audioType: 'question_audio',
        id: formData.optionsFile.id,
      } );
    }
    if ( formData.farewellFile ) {
      mediaAudios.push( {
        audioType: 'closing_audio',
        id: formData.farewellFile.id,
      } );
    }

    toggleLoading( true );
    const [costErrors, costResponse, costResponseData] = await handlePromise(
      CampaignService.validateCost( draftCampaign.id, {
        phoneNumbers: selectedContacts.length,
        mediaAudios,
        rate: formData.rate,
      } ),
    );
    if ( !costResponse.ok ) {
      toggleLoading( false );
      return toggleErrorModal( _.isString( costErrors ) ? costErrors : 'apiError' );
    }

    const cost = formatNumeral(
      selectedContacts.length * ( accountFirstPrice + formData.ratePriceMinute ),
    );
    const calculatedCost = formatNumeral( costResponseData.cost );
    if ( cost !== calculatedCost ) {
      toggleLoading( false );
      return toggleErrorModal( 'apiError' );
    }

    const data = {
      name: formData.name,
      cost: costResponseData.cost,
      time: costResponseData.time,
      type: formData.messageType,
      country: formData.country,
      timeZone: formData.timeZone,
      rate: formData.rate,
      mediaAudios,
      startDateTime: moment.tz( date, 'MM/DD/YYYY HH:mm', true, formData.timeZone ).utc().format( 'DD/MM/YYYY HH:mm' ),
      phoneNumbers: _.map( selectedContacts, ( contact ) => ( {
        nameId: contact.nameId,
        phoneNumber: contact.phoneNumber,
        phoneNumberCountryCode: formData.phoneNumberCountryCode,
        origin: contact.type,
      } ) ),
    };

    const [errors, response] = await handlePromise(
      CampaignService.saveCampaign( draftCampaign.id, data ),
    );
    if ( !response.ok ) {
      toggleLoading( false );
      return toggleErrorModal( _.isString( errors ) ? errors : 'apiError' );
    }

    const [, accountResponse, accountResponseData] = await handlePromise(
      AccountService.getMyAccount(),
    );
    if ( accountResponse.ok ) updateAccountInfo( accountResponseData );

    const [, userResponse, userResponseData] = await handlePromise( UserService.getUser() );
    if ( userResponse.ok ) updateUser( userResponseData );

    toggleLoading( false );
    toggleSuccessModal();
  }, [draftCampaign.id, accountFirstPrice, updateAccountInfo, updateUser,
    toggleLoading, toggleErrorModal, toggleSuccessModal] );


  const [phoneWarningVisible, setPhoneWarningVisible] = useState( false );
  const [phoneWarningLoading, setPhoneWarningLoading] = useState( false );

  const onSubmitCampaign = ( formData ) => {
    if ( user.phone!="UNKNOWN" && user.phone!="" && user.phone!=null ) {
      setBlockNavigationAlert( false );
      submitCampaign( formData );
    } else {
      setPhoneWarningVisible( true );
    }
  };

  const toggleLoadingPhoneWarning = () => {
    setPhoneWarningLoading( ( p ) => !p );
  };

  const onSubmitSenderPhone = useSubmit( {
    promise: ( dataToSend ) => UserService.updateUser( dataToSend ),
    format: ( dataToFormat ) => {
      const dataToSend = formatDataPhoneToSend( dataToFormat, ['phone'] );
      const mainPhoneCountryCode = parsePhoneNumberFromString( dataToFormat.phone );
      return ( {
        ...dataToSend,
        language: locale || 'en',
        firstName: user.firstName,
        lastName: user.lastName,
        phoneCountryCode: mainPhoneCountryCode ? mainPhoneCountryCode.countryCallingCode : null,
      } );
    },
    toggleLoading: toggleLoadingPhoneWarning,
    callback: ( formData, responseData, dToSend ) => {
      updateUser( {
        phone: dToSend.phone ? dToSend.phone : null,
      } );
      setPhoneWarningVisible( false );
    },
  } );

  return (
    <>
      <NewCampaignView
        campaignId={draftCampaign.id}
        // options={{ tariffs}}
         options={{ tariffs, crm }}
        dataTeampleader={dataTeampleader}
        isLoading={dataLoading}
        onSubmit={onSubmitCampaign}
        blockNavigationAlert={blockNavigationAlert}
        setBlockNavigationAlert={setBlockNavigationAlert}
      />
      <PhoneWarning
        visible={phoneWarningVisible}
        onClose={() => { setPhoneWarningVisible( false ); }}
        onSubmit={onSubmitSenderPhone}
        isLoading={phoneWarningLoading}
      />

      {errorModalOpened
      && (
        <ConfirmationModal
          title={errorTextRef.current}
          open={errorModalOpened}
          confirmClose
          onConfirm={toggleErrorModal}
        />
      )}

      {successModalOpened
      && (
        <ConfirmationModal
          title="campaignCreated"
          open={successModalOpened}
          confirmClose
          onConfirm={() => history.push( '/out/campaigns' )}
        />
      )}
    </>
  );
};

const mapStateToProps = ( { account, user, settings } ) => ( {
  user,
  accountFirstPrice: account && account.currentPlan ? account.currentPlan.firstPrice : 0,
  locale: _.get( settings, 'locale.locale' ),
} );

const mapDispatchToProps = ( {
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
  updateAccountInfo: AccountActions.updateInfo,
  updateUser: UserActions.updateUser,
} );

export default connect( mapStateToProps, mapDispatchToProps )( NewCampaign );
